/**
 * Стан стандартного звіту живе В АДРЕСІ (ч.1036): org / from / to + свої
 * ключі кожного звіту. Тому посилання ланцюжка (ОСВ → ОСВ по рахунку →
 * картка) несуть контекст, «назад» повертає до того самого стану, а адресу
 * можна надіслати колезі. Без параметрів — організація з прав, період з
 * шапки («усі дати» → поточний рік).
 *
 * ч.1378 (SUP-1329FE54, клієнт: «збивається все: період, рахунок» при переході в інший розділ
 * меню і назад): адреса без параметрів (пункт меню, вкладка) відкриває звіт з ОСТАННІМИ
 * параметрами цього звіту — вони запамʼятовуються в профілі (`filters:<клієнт>:ledger:<шлях>`,
 * як `useStickyState`), організація перевіряється за правами, як і з адреси.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { stickyKey } from '@/hooks/useStickyFilters';
import { uiPrefs } from '@/store/uiPrefs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaultPeriod } from '@/store/defaults';
import { defaultPeriod, isIsoDate } from './ledgerModel';

export interface LedgerState {
  org: string; from: string; to: string;
  get: (key: string) => string;
  set: (patch: Record<string, string | null | undefined>) => void;
  availableOrgs: { id: string; name: string }[];
  hasPermission: boolean;
  permsLoading: boolean;
  totalAccessibleOrgs: number;
}

export function useLedgerParams(): LedgerState {
  const [urlSp, setSp] = useSearchParams();
  const { pathname } = useLocation();
  const memKey = stickyKey(`ledger:${pathname}`);
  // Адреса без параметрів — останні параметри звіту з профілю (і одразу в адресу).
  const remembered = useMemo(() => {
    if (urlSp.toString()) return null;
    const saved = uiPrefs.get<string>(memKey);
    return typeof saved === 'string' && saved ? saved : null;
  }, [urlSp, memKey]);
  const sp = useMemo(() => (remembered ? new URLSearchParams(remembered) : urlSp), [remembered, urlSp]);
  useEffect(() => {
    if (remembered) setSp(new URLSearchParams(remembered), { replace: true });
  }, [remembered, setSp]);
  useEffect(() => {
    const s = urlSp.toString();
    if (s) uiPrefs.set(memKey, s);
  }, [urlSp, memKey]);
  const { availableOrgs, hasPermissionInAnyOrg, loading, totalAccessibleOrgs } =
    usePermissionsByOrg('reports.regulated.view');
  const { dateFrom, dateTo } = useDefaultPeriod();
  const def = useMemo(() => defaultPeriod(dateFrom, dateTo), [dateFrom, dateTo]);

  const orgParam = sp.get('org') ?? '';
  const org = (orgParam && availableOrgs.some((o) => o.id === orgParam))
    ? orgParam : (availableOrgs[0]?.id ?? '');
  const from = isIsoDate(sp.get('from')) ? sp.get('from')! : def.from;
  const to = isIsoDate(sp.get('to')) ? sp.get('to')! : def.to;

  const set = useCallback((patch: Record<string, string | null | undefined>) => {
    const next = new URLSearchParams(sp);
    // Період і організація — завжди явно в адресі після першої зміни,
    // щоб посилання далі по ланцюжку не залежали від шапки.
    if (!next.get('org') && org) next.set('org', org);
    if (!next.get('from')) next.set('from', from);
    if (!next.get('to')) next.set('to', to);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || v === '') next.delete(k); else next.set(k, v);
    }
    setSp(next, { replace: true });
  }, [sp, setSp, org, from, to]);

  return {
    org, from, to, get: (k) => sp.get(k) ?? '', set,
    availableOrgs, hasPermission: hasPermissionInAnyOrg, permsLoading: loading,
    totalAccessibleOrgs,
  };
}
